import type { ReactNode } from 'react'
import { Fragment } from 'react'

/**
 * Updates an HTML element's content while preserving newlines
 * Converts string newlines (\n) to <br> elements in the DOM
 * We directly change the HTML content of the element passed,
 * to avoid using `dangerouslySetInnerHTML`.
 *
 * @param element - The HTML element to update
 * @param content - The string content with newlines to preserve
 */
export const updateElementContent = (
  element: HTMLElement,
  content: string,
): void => {
  // Clear the element
  element.innerHTML = ''

  // Split by newlines and create content with proper breaks
  const lines = content.split('\n')

  lines.forEach((line, index) => {
    const textNode = document.createTextNode(line)
    element.appendChild(textNode)

    // Add a break after each line except the last one
    if (index < lines.length - 1) {
      element.appendChild(document.createElement('br'))
    }
  })
}

/**
 * Extracts content from an HTML element while preserving newlines
 * Converts <br> and <div> elements to string newlines (\n)
 *
 * @param element - The HTML element to extract content from
 * @returns The extracted string with newlines preserved
 */
export const extractElementContent = (element: HTMLElement): string => {
  // Create a document fragment to work with
  const clone = element.cloneNode(true) as HTMLElement

  // Convert <br> and <div> elements to newlines
  let result = ''
  const childNodes = clone.childNodes

  // Process each node
  for (let i = 0; i < childNodes.length; i++) {
    const node = childNodes[i]

    // Note: Order matters here. BR elements should take favour over other HTML elements
    if (node.nodeType === Node.TEXT_NODE) {
      result += node.textContent
    } else if (node.nodeName === 'BR') {
      result += '\n'
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      result += node.textContent
    } else if (node.nodeName === 'DIV') {
      // For divs (created by pressing enter in contentEditable)
      if (i > 0) {
        result += '\n'
      }
      result += node.textContent
    }
  }

  return result
}

/**
 * Processes a given text string by splitting it into lines based on newline characters ('\n')
 * and non-breaking spaces, and renders each line as a ReactNode wrapped in a <span> element.
 * Adds a <br> element between lines to preserve the original line breaks in the text.
 * Replaces non-breaking spaces with &nbsp;
 *
 * @param {string} text - The input text string containing newlines and non-breaking spaces.
 * @returns {ReactNode} A ReactNode representation of the processed text, where each line is
 * wrapped in a <span> with <br> elements inserted between the lines and non-breaking spaces preserved.
 */
export const extractTextNewLines = (text: string): ReactNode => {
  const newLines = text.split('\n')

  return newLines.map((line, index) => {
    const processedLine = line.replace(/\u00A0/g, '\u0020')

    return (
      <Fragment key={index}>
        <span>{processedLine}</span>
        {index < newLines.length - 1 && <br />}
      </Fragment>
    )
  })
}
